import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api, useBusiness, useAuth } from "@/App";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../components/ui/dialog";
import { toast } from "sonner";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Bookkeeping = () => {
  const navigate = useNavigate();
  const { selectedBusiness } = useBusiness();
  const { token } = useAuth();
  const [dashboard, setDashboard] = useState(null);
  const [recurring, setRecurring] = useState(null);
  const [anomalies, setAnomalies] = useState(null);
  const [importing, setImporting] = useState(false);
  const [categorizing, setCategorizing] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [showImportDialog, setShowImportDialog] = useState(false);
  const [bankAccounts, setBankAccounts] = useState([]);
  const [selectedAccount, setSelectedAccount] = useState("");
  const [loading, setLoading] = useState(true);
  const [customRules, setCustomRules] = useState([]);
  const [showRuleDialog, setShowRuleDialog] = useState(false);
  const [newRule, setNewRule] = useState({ pattern: "", category: "opex", subcategory: "" });
  const [ocrResult, setOcrResult] = useState(null);
  const [ocrLoading, setOcrLoading] = useState(false);
  const [showOcrDialog, setShowOcrDialog] = useState(false);
  const [industryCategories, setIndustryCategories] = useState([]);
  const [industryDeductions, setIndustryDeductions] = useState([]);
  const [deductionSummary, setDeductionSummary] = useState({ total_detected: 0, total_transactions_scanned: 0 });

  const headers = { Authorization: `Bearer ${token}`, "Content-Type": "application/json" };

  const fetchAll = useCallback(async () => {
    if (!selectedBusiness) return;
    setLoading(true);
    try {
      const [dashRes, recRes, anomRes, acctRes, rulesRes] = await Promise.all([
        fetch(`${API_URL}/api/bookkeeping/dashboard/${selectedBusiness.id}`, { headers }),
        fetch(`${API_URL}/api/bookkeeping/recurring/${selectedBusiness.id}`, { headers }),
        fetch(`${API_URL}/api/bookkeeping/anomalies/${selectedBusiness.id}`, { headers }),
        fetch(`${API_URL}/api/manual-bank-accounts/${selectedBusiness.id}`, { headers }),
        fetch(`${API_URL}/api/categorization-rules/${selectedBusiness.id}`, { headers }),
      ]);
      setDashboard(await dashRes.json());
      setRecurring(await recRes.json());
      setAnomalies(await anomRes.json());
      const accounts = await acctRes.json();
      setBankAccounts(Array.isArray(accounts) ? accounts : []);
      if (accounts.length > 0) setSelectedAccount(accounts[0].id);
      const rules = await rulesRes.json();
      setCustomRules(Array.isArray(rules) ? rules : []);
      // Fetch industry categories & deductions
      try {
        const [catRes, dedRes] = await Promise.all([
          fetch(`${API_URL}/api/industries/categories/${selectedBusiness.id}`, { headers }),
          fetch(`${API_URL}/api/industries/deductions/${selectedBusiness.id}`, { headers }),
        ]);
        if (catRes.ok) setIndustryCategories(await catRes.json());
        if (dedRes.ok) {
          const dedData = await dedRes.json();
          setIndustryDeductions(dedData.deductions || []);
          setDeductionSummary({ total_detected: dedData.total_detected || 0, total_transactions_scanned: dedData.total_transactions_scanned || 0 });
        }
      } catch {}
    } catch (err) {
      console.error(err);
    }
    setLoading(false);
  }, [selectedBusiness, token]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const handleAutoCategorize = async () => {
    setCategorizing(true);
    try {
      const res = await fetch(`${API_URL}/api/bookkeeping/auto-categorize/${selectedBusiness.id}`, {
        method: "POST", headers
      });
      const data = await res.json();
      toast.success(`Categorized ${data.categorized} transactions`);
      fetchAll();
    } catch { toast.error("Failed to auto-categorize"); }
    setCategorizing(false);
  };

  const handleImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !selectedAccount) return;
    setImporting(true);
    setImportResult(null);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const res = await fetch(
        `${API_URL}/api/quickbooks/import/${selectedBusiness.id}?bank_account_id=${selectedAccount}`,
        { method: "POST", headers: { Authorization: `Bearer ${token}` }, body: formData }
      );
      const data = await res.json();
      if (res.ok) {
        setImportResult(data);
        toast.success(data.message);
        fetchAll();
      } else {
        toast.error(data.detail || "Import failed");
      }
    } catch { toast.error("Import failed"); }
    setImporting(false);
    e.target.value = "";
  };

  const handleExport = async () => {
    try {
      const res = await fetch(`${API_URL}/api/quickbooks/export/${selectedBusiness.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `opturna_qb_export_${new Date().toISOString().slice(0,10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Export downloaded");
    } catch { toast.error("Export failed"); }
  };

  const handleCreateRule = async () => {
    if (!newRule.pattern || !newRule.category) return;
    try {
      const res = await fetch(`${API_URL}/api/categorization-rules`, {
        method: "POST", headers,
        body: JSON.stringify({ ...newRule, business_id: selectedBusiness.id })
      });
      if (res.ok) {
        toast.success(`Rule created: "${newRule.pattern}" → ${newRule.category}`);
        setNewRule({ pattern: "", category: "opex", subcategory: "" });
        setShowRuleDialog(false);
        fetchAll();
      }
    } catch { toast.error("Failed to create rule"); }
  };

  const handleDeleteRule = async (ruleId) => {
    try {
      await fetch(`${API_URL}/api/categorization-rules/${ruleId}`, {
        method: "DELETE", headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Rule deleted");
      setCustomRules(prev => prev.filter(r => r.id !== ruleId));
    } catch { toast.error("Failed to delete rule"); }
  };

  const handleReceiptOcr = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setOcrLoading(true);
    setOcrResult(null);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const res = await fetch(`${API_URL}/api/receipt-ocr`, {
        method: "POST", headers: { Authorization: `Bearer ${token}` }, body: formData
      });
      const data = await res.json();
      if (data.success) {
        setOcrResult(data.data);
        setShowOcrDialog(true);
        toast.success("Receipt scanned successfully");
      } else {
        toast.error("Could not extract data from receipt");
      }
    } catch { toast.error("OCR failed"); }
    setOcrLoading(false);
    e.target.value = "";
  };

  const fmt = (v) => `$${(v || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  const handleToggleDeduction = async (dedId) => {
    try {
      await fetch(`${API_URL}/api/industries/deductions/${dedId}/claim`, {
        method: "PUT", headers
      });
      setIndustryDeductions(prev => prev.map(d => d.id === dedId ? { ...d, claimed: !d.claimed } : d));
    } catch { toast.error("Failed to update deduction"); }
  };
  if (!selectedBusiness) {
    return (
      <div className="text-center py-20">
        <p className="text-[#5a6577]">Select a business to view bookkeeping tools.</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#1e3a5f]" />
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-6xl" data-testid="bookkeeping-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-[#1e3a5f]">Automated Bookkeeping</h1>
          <p className="text-sm text-[#5a6577] mt-1">Auto-categorize, detect recurring expenses, and sync with QuickBooks</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={handleExport} data-testid="export-qb-btn">
            Export for QuickBooks
          </Button>
          <Button size="sm" className="bg-[#1e3a5f] hover:bg-[#2d5a8e]" onClick={() => setShowImportDialog(true)} data-testid="import-qb-btn">
            Import from QuickBooks
          </Button>
        </div>
      </div>

      {/* Overview Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="card-hover">
          <CardContent className="pt-5 pb-4">
            <p className="text-xs text-[#5a6577] mb-1">Total Transactions</p>
            <p className="text-2xl font-bold text-[#1e3a5f]" data-testid="total-txns">{dashboard?.total_transactions || 0}</p>
          </CardContent>
        </Card>
        <Card className="card-hover">
          <CardContent className="pt-5 pb-4">
            <p className="text-xs text-[#5a6577] mb-1">Needs Categorizing</p>
            <p className="text-2xl font-bold text-[#1e3a5f]" data-testid="uncategorized-count">{dashboard?.uncategorized_count || 0}</p>
            {dashboard?.uncategorized_count > 0 && (
              <Button size="sm" variant="link" className="text-xs p-0 h-auto text-[#1e3a5f]" onClick={handleAutoCategorize} disabled={categorizing}>
                {categorizing ? "Working..." : "Auto-fix"}
              </Button>
            )}
          </CardContent>
        </Card>
        <Card className="card-hover">
          <CardContent className="pt-5 pb-4">
            <p className="text-xs text-[#5a6577] mb-1">Net Income (YTD)</p>
            <p className={`text-2xl font-bold ${(dashboard?.net || 0) >= 0 ? 'text-green-600' : 'text-red-600'}`} data-testid="net-income">
              {fmt(dashboard?.net)}
            </p>
          </CardContent>
        </Card>
        <Card className="card-hover">
          <CardContent className="pt-5 pb-4">
            <p className="text-xs text-[#5a6577] mb-1">Imported</p>
            <p className="text-2xl font-bold text-[#1e3a5f]" data-testid="imported-count">{dashboard?.imported_count || 0}</p>
          </CardContent>
        </Card>
      </div>

      {/* Auto-Categorize Action */}
      {dashboard?.uncategorized_count > 0 && (
        <Card className="border-amber-200 bg-amber-50/50">
          <CardContent className="py-4 flex items-center justify-between">
            <div>
              <p className="font-medium text-amber-800">{dashboard.uncategorized_count} transactions need categorizing</p>
              <p className="text-xs text-amber-600 mt-0.5">Our rule engine can automatically categorize them based on vendor patterns</p>
            </div>
            <Button className="bg-amber-600 hover:bg-amber-700" onClick={handleAutoCategorize} disabled={categorizing} data-testid="auto-categorize-btn">
              {categorizing ? "Categorizing..." : "Auto-Categorize All"}
            </Button>
          </CardContent>
        </Card>
      )}

      <div className="grid md:grid-cols-2 gap-6">
        {/* Recurring Expenses */}
        <Card data-testid="recurring-expenses-card">
          <CardHeader className="pb-3">
            <CardTitle className="text-base text-[#1e3a5f]">Recurring Expenses</CardTitle>
            <CardDescription className="text-xs">
              {recurring?.total_recurring_count || 0} detected
              {recurring?.estimated_monthly_recurring > 0 && ` | ${fmt(recurring.estimated_monthly_recurring)}/mo`}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {recurring?.recurring_expenses?.length > 0 ? (
              <div className="space-y-2 max-h-72 overflow-y-auto">
                {recurring.recurring_expenses.slice(0, 10).map((r, i) => (
                  <div key={i} className="flex items-center justify-between p-2.5 rounded-lg bg-[#f5f7fa]">
                    <div>
                      <p className="text-sm font-medium text-[#1e3a5f]">{r.vendor}</p>
                      <p className="text-xs text-[#5a6577]">
                        {r.occurrences}x over {r.months.length} months
                        <Badge variant="outline" className="ml-2 text-[10px]">{r.frequency}</Badge>
                      </p>
                    </div>
                    <p className="font-semibold text-[#1e3a5f]">{fmt(r.amount)}</p>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-[#5a6577] text-center py-6">No recurring patterns detected yet. Add more transactions to see patterns.</p>
            )}
          </CardContent>
        </Card>

        {/* Anomalies / Unusual Spending */}
        <Card data-testid="anomalies-card">
          <CardHeader className="pb-3">
            <CardTitle className="text-base text-[#1e3a5f]">Unusual Spending Alerts</CardTitle>
            <CardDescription className="text-xs">{anomalies?.total_anomalies || 0} anomalies found</CardDescription>
          </CardHeader>
          <CardContent>
            {anomalies?.anomalies?.length > 0 ? (
              <div className="space-y-2 max-h-72 overflow-y-auto">
                {anomalies.anomalies.slice(0, 10).map((a, i) => (
                  <div key={i} className={`p-2.5 rounded-lg ${a.severity === 'high' ? 'bg-red-50' : 'bg-amber-50'}`}>
                    <div className="flex items-center gap-2 mb-1">
                      <Badge className={a.severity === 'high' ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'}>
                        {a.severity === 'high' ? 'High' : 'Medium'}
                      </Badge>
                      <span className="text-xs text-[#5a6577]">{a.month}</span>
                    </div>
                    <p className="text-sm text-[#1e3a5f]">{a.message}</p>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-[#5a6577] text-center py-6">No unusual spending detected. Your expenses look consistent.</p>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Custom Categorization Rules */}
      <Card data-testid="custom-rules-card">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-base text-[#1e3a5f]">Custom Categorization Rules</CardTitle>
              <CardDescription className="text-xs">Your rules take priority over built-in patterns</CardDescription>
            </div>
            <Button size="sm" className="bg-[#1e3a5f] hover:bg-[#2d5a8e]" onClick={() => setShowRuleDialog(true)} data-testid="add-rule-btn">
              Add Rule
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {customRules.length > 0 ? (
            <div className="space-y-2">
              {customRules.map((rule) => (
                <div key={rule.id} className="flex items-center justify-between p-2.5 rounded-lg bg-[#f5f7fa]">
                  <div className="flex items-center gap-3">
                    <code className="text-xs bg-[#1e3a5f]/10 text-[#1e3a5f] px-2 py-0.5 rounded">{rule.pattern}</code>
                    <svg className="w-4 h-4 text-[#8b95a5]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><path d="M5 12h14M12 5l7 7-7 7"/></svg>
                    <Badge variant="outline" className="text-xs">{rule.category}</Badge>
                    {rule.subcategory && <span className="text-xs text-[#5a6577]">{rule.subcategory}</span>}
                  </div>
                  <Button variant="ghost" size="sm" className="text-red-500 hover:text-red-700 hover:bg-red-50 h-7 px-2"
                    onClick={() => handleDeleteRule(rule.id)} data-testid={`delete-rule-${rule.id}`}>
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.5"><path d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"/></svg>
                  </Button>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-[#5a6577] text-center py-4">No custom rules yet. Add rules like "starbucks" → Meals to auto-sort your transactions.</p>
          )}
        </CardContent>
      </Card>

      {/* Industry Categories */}
      {industryCategories.length > 0 && (
        <Card data-testid="industry-categories-card">
          <CardHeader className="pb-3">
            <div>
              <CardTitle className="text-base text-[#1e3a5f]">Your Expense Categories</CardTitle>
              <CardDescription className="text-xs">Pre-loaded for your industry — use these when categorizing transactions</CardDescription>
            </div>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-2">
              {industryCategories.map((cat) => (
                <span key={cat.id} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[#1e3a5f]/5 border border-[#1e3a5f]/10 text-sm text-[#1e3a5f]" data-testid={`industry-cat-${cat.name}`}>
                  <span className="w-2 h-2 rounded-full bg-[#1e3a5f]" />
                  {cat.name}
                </span>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Industry Deductions */}
      {industryDeductions.length > 0 && (
        <Card data-testid="industry-deductions-card">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-base text-[#1e3a5f]">Estimated Deductions</CardTitle>
                <CardDescription className="text-xs">Auto-detected from your transactions — {deductionSummary.total_transactions_scanned} transactions scanned</CardDescription>
              </div>
              {deductionSummary.total_detected > 0 && (
                <div className="text-right" data-testid="total-detected-amount">
                  <p className="text-xs text-[#5a6577]">Total Detected</p>
                  <p className="text-xl font-bold text-[#1e3a5f]">{fmt(deductionSummary.total_detected)}</p>
                </div>
              )}
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {industryDeductions.map((ded) => (
                <div key={ded.id} className="flex items-start gap-3 p-3 rounded-lg bg-[#f5f7fa] hover:bg-[#edf0f5] transition-colors cursor-pointer"
                  onClick={() => handleToggleDeduction(ded.id)} data-testid={`deduction-${ded.id}`}>
                  <div className={`mt-0.5 w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 transition-colors ${ded.claimed ? "bg-[#1e3a5f] border-[#1e3a5f]" : "border-[#c4cdd8] bg-white"}`}>
                    {ded.claimed && (
                      <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="3">
                        <polyline points="20 6 9 17 4 12" />
                      </svg>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2">
                      <p className={`text-sm font-medium ${ded.claimed ? "text-[#1e3a5f]" : "text-[#3d4f63]"}`}>{ded.name}</p>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        {ded.detected_amount > 0 ? (
                          <span className="text-sm font-semibold text-[#1e3a5f]" data-testid={`detected-amt-${ded.id}`}>{fmt(ded.detected_amount)}</span>
                        ) : (
                          <span className="text-[11px] text-[#8b95a5] italic">No transactions yet</span>
                        )}
                      </div>
                    </div>
                    <p className="text-xs text-[#5a6577] mt-0.5">{ded.description}</p>
                    <div className="flex items-center gap-3 mt-1.5">
                      <Badge variant="outline" className="text-[10px]">{ded.category}</Badge>
                      {ded.matching_transactions > 0 && (
                        <span className="text-[10px] text-[#4a9eff]" data-testid={`txn-count-${ded.id}`}>
                          {ded.matching_transactions} transaction{ded.matching_transactions !== 1 ? "s" : ""} matched
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
            <div className="flex items-center justify-between mt-4 pt-3 border-t border-[#e4e8ee]">
              <span className="text-xs text-[#5a6577]">{industryDeductions.filter(d => d.claimed).length} of {industryDeductions.length} claimed</span>
              <div className="flex items-center gap-4">
                <span className="text-xs text-[#5a6577]">{industryDeductions.filter(d => d.detected_amount > 0).length} with transactions</span>
                <span className="text-xs font-medium text-[#1e3a5f]">{Math.round(industryDeductions.filter(d => d.claimed).length / industryDeductions.length * 100)}% coverage</span>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Receipt OCR Scanner */}
      <Card data-testid="receipt-ocr-card">
        <CardHeader className="pb-3">
          <CardTitle className="text-base text-[#1e3a5f]">Receipt Scanner (OCR)</CardTitle>
          <CardDescription className="text-xs">Upload a receipt photo to auto-extract vendor, amount, and date</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-4">
            <label className="flex-1 cursor-pointer">
              <div className="border-2 border-dashed border-[#1e3a5f]/20 rounded-lg p-6 text-center hover:border-[#1e3a5f]/40 transition-colors">
                <svg className="w-8 h-8 text-[#1e3a5f]/40 mx-auto mb-2" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.5">
                  <path d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z"/>
                  <circle cx="12" cy="13" r="3"/>
                </svg>
                <p className="text-sm text-[#1e3a5f] font-medium">{ocrLoading ? "Scanning..." : "Upload Receipt Image"}</p>
                <p className="text-xs text-[#8b95a5] mt-1">JPG, PNG, or HEIC up to 10MB</p>
              </div>
              <input type="file" accept="image/*" className="hidden" onChange={handleReceiptOcr} disabled={ocrLoading} data-testid="ocr-file-input" />
            </label>
            {ocrResult && (
              <div className="flex-1 p-4 rounded-lg bg-[#1e3a5f]/5 border border-[#1e3a5f]/10" data-testid="ocr-result">
                <p className="text-xs font-semibold text-[#1e3a5f] mb-2">Extracted Data</p>
                <div className="space-y-1.5 text-sm">
                  <div className="flex justify-between">
                    <span className="text-[#5a6577]">Vendor</span>
                    <span className="font-medium text-[#1e3a5f]">{ocrResult.vendor || "—"}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-[#5a6577]">Amount</span>
                    <span className="font-medium text-[#1e3a5f]">{ocrResult.amount != null ? fmt(ocrResult.amount) : "—"}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-[#5a6577]">Date</span>
                    <span className="font-medium text-[#1e3a5f]">{ocrResult.date || "—"}</span>
                  </div>
                  {ocrResult.tax != null && ocrResult.tax > 0 && (
                    <div className="flex justify-between">
                      <span className="text-[#5a6577]">Tax</span>
                      <span className="font-medium text-[#1e3a5f]">{fmt(ocrResult.tax)}</span>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* QuickBooks Import/Export Section */}
      <Card data-testid="quickbooks-section">
        <CardHeader>
          <CardTitle className="text-base text-[#1e3a5f]">QuickBooks Compatibility</CardTitle>
          <CardDescription className="text-xs">Import transactions from QuickBooks or export your data in QuickBooks format</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid md:grid-cols-2 gap-6">
            {/* Import */}
            <div className="p-4 rounded-lg border border-dashed border-[#1e3a5f]/20 bg-[#1e3a5f]/[0.02]">
              <div className="text-center">
                <div className="w-12 h-12 rounded-full bg-[#1e3a5f]/10 flex items-center justify-center mx-auto mb-3">
                  <svg className="w-6 h-6 text-[#1e3a5f]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.5">
                    <path d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                  </svg>
                </div>
                <h3 className="font-medium text-[#1e3a5f] mb-1">Import from QuickBooks</h3>
                <p className="text-xs text-[#5a6577] mb-3">Upload a .CSV or .QBO file exported from QuickBooks</p>
                <Button size="sm" className="bg-[#1e3a5f] hover:bg-[#2d5a8e]" onClick={() => setShowImportDialog(true)} data-testid="import-btn">
                  Upload File
                </Button>
              </div>
            </div>
            {/* Export */}
            <div className="p-4 rounded-lg border border-dashed border-[#1e3a5f]/20 bg-[#1e3a5f]/[0.02]">
              <div className="text-center">
                <div className="w-12 h-12 rounded-full bg-[#1e3a5f]/10 flex items-center justify-center mx-auto mb-3">
                  <svg className="w-6 h-6 text-[#1e3a5f]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.5">
                    <path d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                </div>
                <h3 className="font-medium text-[#1e3a5f] mb-1">Export for QuickBooks</h3>
                <p className="text-xs text-[#5a6577] mb-3">Download your transactions in QuickBooks-compatible CSV format</p>
                <Button size="sm" variant="outline" onClick={handleExport} data-testid="export-btn">
                  Download CSV
                </Button>
              </div>
            </div>
          </div>

          {/* Last import result */}
          {importResult && (
            <div className="mt-4 p-3 rounded-lg bg-[#1e3a5f]/5 border border-[#1e3a5f]/10">
              <p className="text-sm font-medium text-[#1e3a5f]">Last Import Result</p>
              <div className="grid grid-cols-3 gap-4 mt-2 text-xs">
                <div>
                  <p className="text-[#5a6577]">Imported</p>
                  <p className="font-semibold text-[#1e3a5f]">{importResult.imported}</p>
                </div>
                <div>
                  <p className="text-[#5a6577]">Auto-Categorized</p>
                  <p className="font-semibold text-[#1e3a5f]">{importResult.auto_categorized}</p>
                </div>
                <div>
                  <p className="text-[#5a6577]">Skipped (duplicates)</p>
                  <p className="font-semibold text-[#5a6577]">{importResult.skipped}</p>
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Add Rule Dialog */}
      <Dialog open={showRuleDialog} onOpenChange={setShowRuleDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-[#1e3a5f]">Create Categorization Rule</DialogTitle>
            <DialogDescription className="text-xs">
              When a transaction description contains your pattern, it will be auto-categorized.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <label className="text-sm font-medium text-[#1e3a5f] block mb-1.5">Vendor Pattern</label>
              <input
                type="text"
                placeholder='e.g. "starbucks" or "amazon"'
                value={newRule.pattern}
                onChange={(e) => setNewRule(prev => ({ ...prev, pattern: e.target.value }))}
                className="w-full px-3 py-2 rounded-md border border-[#e4e8ee] text-sm focus:outline-none focus:ring-1 focus:ring-[#1e3a5f]/20"
                data-testid="rule-pattern-input"
              />
              <p className="text-[10px] text-[#8b95a5] mt-1">Case-insensitive. Matches anywhere in the description.</p>
            </div>
            <div>
              <label className="text-sm font-medium text-[#1e3a5f] block mb-1.5">Category</label>
              <select
                value={newRule.category}
                onChange={(e) => setNewRule(prev => ({ ...prev, category: e.target.value }))}
                className="w-full px-3 py-2 rounded-md border border-[#e4e8ee] text-sm focus:outline-none focus:ring-1 focus:ring-[#1e3a5f]/20"
                data-testid="rule-category-select"
              >
                <optgroup label="Allocation Categories">
                  <option value="income">Income / Revenue</option>
                  <option value="opex">Operating Expense</option>
                  <option value="tax">Tax</option>
                  <option value="profit">Profit</option>
                  <option value="owner_pay">Owner's Pay</option>
                </optgroup>
                <optgroup label="Office & Workspace">
                  <option value="rent">Rent</option>
                  <option value="utilities">Utilities</option>
                  <option value="office_supplies">Office Supplies</option>
                  <option value="home_office">Home Office</option>
                </optgroup>
                <optgroup label="Technology & Equipment">
                  <option value="software">Software & Subscriptions</option>
                  <option value="equipment">Equipment & Hardware</option>
                </optgroup>
                <optgroup label="Marketing & Sales">
                  <option value="marketing">Marketing & Advertising</option>
                </optgroup>
                <optgroup label="Travel & Meals">
                  <option value="travel">Travel & Transportation</option>
                  <option value="meals">Meals & Entertainment</option>
                </optgroup>
                <optgroup label="Professional Services">
                  <option value="professional_services">Professional Services</option>
                  <option value="legal">Legal & Compliance</option>
                  <option value="accounting">Accounting & Bookkeeping</option>
                </optgroup>
                <optgroup label="People & Payroll">
                  <option value="payroll">Employee & Contractor Costs</option>
                  <option value="education">Education & Training</option>
                </optgroup>
                <optgroup label="Insurance & Finance">
                  <option value="insurance">Insurance</option>
                  <option value="banking_fees">Banking & Finance Fees</option>
                  <option value="interest">Loan Interest</option>
                </optgroup>
                <optgroup label="Other">
                  <option value="shipping">Shipping & Delivery</option>
                  <option value="inventory">Inventory & Cost of Goods</option>
                  <option value="depreciation">Depreciation & Amortization</option>
                  <option value="charitable">Charitable & Community</option>
                  <option value="supplies">Supplies</option>
                  <option value="uncategorized">Uncategorized</option>
                </optgroup>
              </select>
            </div>
            <div>
              <label className="text-sm font-medium text-[#1e3a5f] block mb-1.5">Subcategory (optional)</label>
              <input
                type="text"
                placeholder="e.g. Meals & Entertainment"
                value={newRule.subcategory}
                onChange={(e) => setNewRule(prev => ({ ...prev, subcategory: e.target.value }))}
                className="w-full px-3 py-2 rounded-md border border-[#e4e8ee] text-sm focus:outline-none focus:ring-1 focus:ring-[#1e3a5f]/20"
                data-testid="rule-subcategory-input"
              />
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" size="sm" onClick={() => setShowRuleDialog(false)} data-testid="rule-cancel-btn">Cancel</Button>
              <Button size="sm" className="bg-[#1e3a5f] hover:bg-[#2d5a8e]" onClick={handleCreateRule}
                disabled={!newRule.pattern || !newRule.category} data-testid="rule-save-btn">
                Save Rule
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Import Dialog */}
      <Dialog open={showImportDialog} onOpenChange={setShowImportDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-[#1e3a5f]">Import QuickBooks File</DialogTitle>
            <DialogDescription className="text-xs">
              Upload a .CSV or .QBO file exported from QuickBooks Online or Desktop
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <label className="text-sm font-medium text-[#1e3a5f] block mb-1.5">Target Bank Account</label>
              <select
                value={selectedAccount}
                onChange={(e) => setSelectedAccount(e.target.value)}
                className="w-full px-3 py-2 rounded-md border border-[#e4e8ee] text-sm focus:outline-none focus:ring-1 focus:ring-[#1e3a5f]/20"
                data-testid="import-account-select"
              >
                {bankAccounts.length === 0 && <option value="">No accounts — create one first</option>}
                {bankAccounts.map(a => (
                  <option key={a.id} value={a.id}>{a.account_name} ({a.bank_name})</option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-sm font-medium text-[#1e3a5f] block mb-1.5">File (.CSV or .QBO)</label>
              <input
                type="file"
                accept=".csv,.qbo,.ofx"
                onChange={handleImport}
                disabled={importing || !selectedAccount}
                className="w-full text-sm file:mr-3 file:py-2 file:px-4 file:rounded-md file:border-0 file:bg-[#1e3a5f] file:text-white file:text-sm file:font-medium hover:file:bg-[#2d5a8e] file:cursor-pointer"
                data-testid="import-file-input"
              />
            </div>
            {importing && (
              <div className="flex items-center gap-2 text-sm text-[#5a6577]">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-[#1e3a5f]" />
                Importing...
              </div>
            )}
            <p className="text-[10px] text-[#8b95a5]">
              Supported formats: QuickBooks Online CSV, QuickBooks Desktop CSV, QBO/OFX bank feeds.
              Duplicate transactions are automatically skipped.
            </p>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Bookkeeping;
